'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import type { ReactNode } from 'react'
import {
  MARKETING_CSS, Icon, ICONS,
  NavBar, MarketingFooter, useMarketingReveal,
} from '../marketing-ui'

// ─── Page-specific styles ─────────────────────────────────────────────────────
const FEATURES_CSS = `
.ft-layout { display: flex; gap: 56px; align-items: flex-start; }
.ft-sidebar { position: sticky; top: 88px; width: 200px; flex-shrink: 0; }
.ft-sidebar a {
  display: flex; align-items: center; gap: 10px;
  padding: 7px 10px; border-radius: 7px; text-decoration: none;
  font-size: 13px; font-weight: 500; color: var(--mk-body);
  transition: background-color 0.15s, color 0.15s;
}
.ft-sidebar a:hover { background: var(--mk-line-soft); color: var(--mk-ink); }
.ft-sidebar a.is-active { background: var(--mk-green-soft); color: var(--mk-green); font-weight: 600; }
.ft-section { scroll-margin-top: 92px; }
@media (max-width: 860px) {
  .ft-sidebar { display: none; }
}
`

// ─── Types & content (copy unchanged from the original tour) ─────────────────

interface Section {
  id: string
  label: string
  icon: ReactNode
}

interface FeatureItem {
  icon: ReactNode
  title: string
  description: string
  section: string
}

const NAV_SECTIONS: Section[] = [
  { id: 'getting-started', label: 'Getting Started', icon: ICONS.home },
  { id: 'products',        label: 'Product Dev',     icon: ICONS.shirt },
  { id: 'manufacturing',   label: 'Development',     icon: ICONS.layers },
  { id: 'orders',          label: 'Production',      icon: ICONS.box },
  { id: 'content',         label: 'Content Studio',  icon: ICONS.pen },
  { id: 'contacts',        label: 'Contacts',        icon: ICONS.users },
  { id: 'library',         label: 'Library',         icon: ICONS.book },
  { id: 'tasks',           label: 'Tasks',           icon: ICONS.checkSquare },
  { id: 'reports',         label: 'Reports',         icon: ICONS.chart },
  { id: 'settings',        label: 'Settings & Team', icon: ICONS.gear },
]

const STEPS = [
  {
    number: '01',
    title: 'Set up your brand',
    description:
      'Head to Settings → Brand Profile to add your brand name, logo, colors, and store links. This personalizes your entire workspace.',
  },
  {
    number: '02',
    title: 'Invite your team',
    description:
      'Go to Settings → Team to invite collaborators. Assign roles — Owner, Team Member, Designer, or Social Media — to control what each person can see and do.',
  },
  {
    number: '03',
    title: 'Add your products',
    description:
      'Start in Product Dev to build out your catalog. Create styles, assign colorways, and link them to tech packs and blanks in Development.',
  },
  {
    number: '04',
    title: 'Track production',
    description:
      'Use Development to manage tech packs, colorway sheets, sample logs, and blank sourcing — all tied to your products.',
  },
  {
    number: '05',
    title: 'Manage orders',
    description:
      'Log purchase orders and track costs in Production. The dashboard gives you a live view of spend across all active production runs.',
  },
  {
    number: '06',
    title: 'Plan content & campaigns',
    description:
      'Content Studio is your creative pipeline. Capture ideas, brief shoots, and track content from concept to published.',
  },
]

const FEATURE_ITEMS: FeatureItem[] = [
  // Getting Started
  {
    section: 'getting-started',
    icon: ICONS.home,
    title: 'Home Dashboard',
    description:
      'Your daily command center. See upcoming tasks, recent activity, open orders, and quick links to the sections you use most.',
  },
  {
    section: 'getting-started',
    icon: ICONS.message,
    title: 'Inbox',
    description:
      'Stay on top of @mentions, task assignments, and status changes. Filter by All, Unread, or Mentions — from the bell for a quick peek, or the full Inbox page.',
  },
  {
    section: 'getting-started',
    icon: ICONS.search,
    title: 'Global Search',
    description:
      'Press ⌘K anywhere in the app to instantly search across products, orders, tasks, contacts, and more.',
  },
  // Product Dev
  {
    section: 'products',
    icon: ICONS.shirt,
    title: 'Product Catalog',
    description:
      'Build a full catalog of styles with gender, category, season, and status. Each product links directly to its tech pack and colorways.',
  },
  {
    section: 'products',
    icon: ICONS.star,
    title: 'Launch Prep',
    description:
      'Pre-launch readiness across six categories — Setup, Content, Media, Merch, Marketing, and Final. Spot blockers before they slip a release.',
  },
  {
    section: 'products',
    icon: ICONS.camera,
    title: 'Inspiration Boards',
    description:
      'Mood boards for your design team. Pin references, drop in images, link to source. The visual brief for what you\'re building next.',
  },
  // Manufacturing
  {
    section: 'manufacturing',
    icon: ICONS.sheet,
    title: 'Tech Packs (BTPs)',
    description:
      'Blank Tech Packs — the master production spec for each silhouette. Construction, fabrics, measurements. One BTP feeds many colorway sheets.',
  },
  {
    section: 'manufacturing',
    icon: ICONS.layers,
    title: 'Colorway Sheets (CS)',
    description:
      'One CS = one BTP × one colorway. A crew tee BTP might have five CSs across Black, Heather, Cream, Olive, and Navy.',
  },
  {
    section: 'manufacturing',
    icon: ICONS.shirt,
    title: 'Blank Garments',
    description:
      'Pre-sourced blanks you private-label — alternative to custom tech packs. Track supplier, MOQ, and cost per piece.',
  },
  {
    section: 'manufacturing',
    icon: ICONS.pen,
    title: 'Designs',
    description:
      'Artwork, logos, and graphic assets. Reusable across products and blanks. Version-controlled with approval status.',
  },
  {
    section: 'manufacturing',
    icon: ICONS.checkSquare,
    title: 'Sample Rounds',
    description:
      'Three rounds of vendor samples per style. Approve, reject, request changes, flag for review, or hold pending the vendor.',
  },
  // Orders
  {
    section: 'orders',
    icon: ICONS.box,
    title: 'Purchase Orders',
    description:
      'Track POs from Planning → Sampling → Review → Production → Shipping → Delivered. Line items capture product × colorway × size.',
  },
  {
    section: 'orders',
    icon: ICONS.coins,
    title: 'Cost Math',
    description:
      'Order totals roll up from line-item totals, not unit cost × qty. Discounts, freight, and adjustments stay clean and auditable.',
  },
  {
    section: 'orders',
    icon: ICONS.store,
    title: 'Vendor Portal',
    description:
      'Send vendors a token-authenticated link — they see only their orders, upload samples, post updates. No login required on their end.',
  },
  // Content Studio
  {
    section: 'content',
    icon: ICONS.pen,
    title: 'Content Cards',
    description:
      'Plan and approve social posts. Each card is a post — image, caption, channel, scheduled date. Move through your pipeline from Idea to Ready to Post.',
  },
  {
    section: 'content',
    icon: ICONS.layers,
    title: 'Pipeline Stages',
    description:
      'Posts flow Idea → Briefing → Writing → Creative → Filming → Editing → Review → Approved → Ready to Post. Each stage has its own owner.',
  },
  {
    section: 'content',
    icon: ICONS.camera,
    title: 'Instagram Preview',
    description:
      'Live mockup of how a post will look on your feed — using your brand handle and avatar. Catches typos and cropping issues before publish.',
  },
  {
    section: 'content',
    icon: ICONS.chart,
    title: 'Ad Library',
    description:
      'Inspiration board for paid creative — plus AI-assisted variants you can spin off and brief out.',
  },
  // Contacts
  {
    section: 'contacts',
    icon: ICONS.users,
    title: 'Contacts',
    description:
      'Your contact book — vendors, suppliers, collaborators. Tag by type, attach docs, log communication history.',
  },
  {
    section: 'contacts',
    icon: ICONS.star,
    title: 'Creators & Ambassadors',
    description:
      'Manage your creator and ambassador roster. Assign products, track deliverables, capture content. Pairs with the public Ambassador Portal.',
  },
  // Library
  {
    section: 'library',
    icon: ICONS.folder,
    title: 'File Vault',
    description:
      'Everything you\'ve ever uploaded — tech packs, designs, photography, order docs. Filter by type, by linked record, by uploader.',
  },
  {
    section: 'library',
    icon: ICONS.book,
    title: 'Brand Guide',
    description:
      'Your living brand bible — typography, color, logo usage, tone of voice. Keep everyone designing on the same page.',
  },
  {
    section: 'library',
    icon: ICONS.camera,
    title: 'Photography Workspace',
    description:
      'Organize shoots end-to-end — pre-pro planning, shot lists, on-set capture, post selection, and channel-by-channel approvals.',
  },
  {
    section: 'library',
    icon: ICONS.book,
    title: 'Resources & Knowledge',
    description:
      'A knowledge base for your brand. SOPs, vendor contacts, playbooks — anything your team needs in one searchable spot.',
  },
  // Tasks
  {
    section: 'tasks',
    icon: ICONS.checkSquare,
    title: 'Tasks',
    description:
      'Universal task system. Link tasks to products, tech packs, content cards, or orders. Filter by owner, due date, and priority.',
  },
  {
    section: 'tasks',
    icon: ICONS.check,
    title: 'Approvals & Followers',
    description:
      'Approval is distinct from completion — a task can be approved by the reviewer but still owned by the assignee. Follow records to stay in the loop.',
  },
  // Reports
  {
    section: 'reports',
    icon: ICONS.chart,
    title: 'Reports & Metrics',
    description:
      'Cross-section reports and live KPIs — lead times, on-time delivery, margin by style, season recaps. Drill into what shipped and what slipped.',
  },
  // Settings
  {
    section: 'settings',
    icon: ICONS.gear,
    title: 'Brand Profile',
    description:
      'Configure your brand name, logo, favicon, website URL, and social handles. These populate throughout the app.',
  },
  {
    section: 'settings',
    icon: ICONS.layers,
    title: 'Workspace Modules',
    description:
      'Turn modules on or off — Production, Content Studio, Ad Library, Contacts, Creators, Reports. Customize the app to your workflow.',
  },
  {
    section: 'settings',
    icon: ICONS.users,
    title: 'Roles & Permissions',
    description:
      'Control what each role can access. Owners have full access; Designers focus on manufacturing and product; Social Media is scoped to content.',
  },
]

// ─── Building blocks ──────────────────────────────────────────────────────────

function FeatureRow({ item }: { item: FeatureItem }) {
  return (
    <div className="mk-ledger-row">
      <div className="mk-icon-tile">
        <Icon d={item.icon} />
      </div>
      <div>
        <h3 style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--mk-ink)', margin: '0 0 6px' }}>{item.title}</h3>
        <p style={{ fontSize: 14, color: 'var(--mk-body)', lineHeight: 1.6, margin: 0 }}>{item.description}</p>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FeaturesPage({ displayFontClass = '' }: { displayFontClass?: string }) {
  const rootRef = useRef<HTMLDivElement>(null)
  useMarketingReveal(rootRef)

  const [activeSection, setActiveSection] = useState('getting-started')
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})

  // Track the section in view for the sidebar
  useEffect(() => {
    const observers: IntersectionObserver[] = []
    NAV_SECTIONS.forEach(sec => {
      const el = sectionRefs.current[sec.id]
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(sec.id) },
        { rootMargin: '-20% 0px -60% 0px' },
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach(o => o.disconnect())
  }, [])

  const itemsBySection = (id: string) => FEATURE_ITEMS.filter(c => c.section === id)

  return (
    <div ref={rootRef} className={`mk ${displayFontClass}`}>
      <style>{MARKETING_CSS}</style>
      <style>{FEATURES_CSS}</style>
      <NavBar />
      <main>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section style={{ paddingTop: 140, paddingBottom: 64 }}>
          <div className="mk-container" style={{ textAlign: 'center', maxWidth: 760 }}>
            <p className="mk-eyebrow mk-eyebrow--center">The full tour</p>
            <h1 className="mk-display" style={{ fontSize: 'clamp(36px, 4.6vw, 56px)', marginBottom: 20 }}>
              Everything Obi-Hub does, <em>in one place</em>
            </h1>
            <p className="mk-lead" style={{ maxWidth: 580, margin: '0 auto 32px' }}>
              From setting up your brand to managing production and content — a walkthrough of
              every part of the platform.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link href="/#beta" className="mk-btn">Apply for beta access</Link>
              <a href="#getting-started" className="mk-btn mk-btn--ghost">Start the tour</a>
            </div>
          </div>
        </section>

        {/* ── Getting started ──────────────────────────────────────────── */}
        <section
          id="getting-started"
          ref={el => { sectionRefs.current['getting-started'] = el }}
          className="mk-section mk-section--white mk-stitch-top ft-section"
          data-reveal
          style={{ paddingTop: 72, paddingBottom: 56 }}
        >
          <div className="mk-container">
            <div style={{ maxWidth: 620, marginBottom: 20 }}>
              <h2 className="mk-display" style={{ fontSize: 'clamp(28px, 3.2vw, 38px)', marginBottom: 12 }}>
                Up and running in <em>six steps</em>
              </h2>
              <p className="mk-lead" style={{ fontSize: 16 }}>
                Follow these in order to get your brand fully set up and your team working in Obi-Hub.
              </p>
            </div>
            <div className="mk-ledger" data-reveal data-stagger>
              {STEPS.map(step => (
                <div key={step.number} className="mk-ledger-row">
                  <div className="mk-display" style={{
                    fontSize: 22, color: 'var(--mk-green)', fontStyle: 'italic',
                    width: 40, flexShrink: 0, lineHeight: 1.3,
                  }}>
                    {step.number}
                  </div>
                  <div>
                    <h3 style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--mk-ink)', margin: '0 0 6px' }}>{step.title}</h3>
                    <p style={{ fontSize: 14, color: 'var(--mk-body)', lineHeight: 1.6, margin: 0 }}>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mk-ledger" data-reveal data-stagger style={{ marginTop: 8 }}>
              {itemsBySection('getting-started').map(item => (
                <FeatureRow key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* ── Sidebar + module sections ────────────────────────────────── */}
        <section className="mk-section mk-stitch-top" data-reveal style={{ paddingTop: 64 }}>
          <div className="mk-container ft-layout">
            <nav className="ft-sidebar" aria-label="Feature sections">
              <div style={{
                fontSize: 11, fontWeight: 700, color: 'var(--mk-faint)', textTransform: 'uppercase',
                letterSpacing: '0.1em', marginBottom: 10, paddingLeft: 10,
              }}>
                Sections
              </div>
              {NAV_SECTIONS.map(sec => (
                <a key={sec.id} href={`#${sec.id}`} className={activeSection === sec.id ? 'is-active' : ''}>
                  <Icon d={sec.icon} size={15} />
                  {sec.label}
                </a>
              ))}
            </nav>

            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 56 }}>
              {NAV_SECTIONS.filter(s => s.id !== 'getting-started').map(sec => (
                <section
                  key={sec.id}
                  id={sec.id}
                  ref={el => { sectionRefs.current[sec.id] = el }}
                  className="ft-section"
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    marginBottom: 8, paddingBottom: 14,
                    borderBottom: '1px dashed var(--mk-line)',
                  }}>
                    <div className="mk-icon-tile" style={{ width: 34, height: 34, borderRadius: 9 }}>
                      <Icon d={sec.icon} size={17} />
                    </div>
                    <h2 className="mk-display" style={{ fontSize: 24, margin: 0 }}>
                      {sec.label}
                    </h2>
                  </div>
                  <div className="mk-ledger">
                    {itemsBySection(sec.id).map(item => (
                      <FeatureRow key={item.title} item={item} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────────────────── */}
        <section className="mk-section mk-section--white mk-stitch-top" data-reveal style={{ padding: '72px 24px' }}>
          <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
            <h2 className="mk-display" style={{ fontSize: 'clamp(26px, 3vw, 36px)', marginBottom: 14 }}>
              See it with <em>your own products</em>
            </h2>
            <p className="mk-lead" style={{ fontSize: 16, marginBottom: 28 }}>
              We&apos;re a small team and we actually read every email. Apply for the beta, or reach
              out with questions and we&apos;ll get back to you quickly.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
              <Link href="/#beta" className="mk-btn">Apply for beta access</Link>
              <a href="mailto:support@obi-hub.app" className="mk-btn mk-btn--ghost">Email us</a>
            </div>
          </div>
        </section>

      </main>
      <MarketingFooter />
    </div>
  )
}
